/*
 * P2PTrailPanel — the redesigned agent action trail.
 *
 * A React port of sidebar-redesign/sidebar-mockup.html. Fetches
 *   GET /api/p2p/exceptions/:id/trail
 * (see AGENT_TRAIL_SIDEBAR_AUDIT.md §6) and renders a fixed Summary block,
 * a run header, and four tabs — All / Processing / SOPs / Communications —
 * all projected client-side from one flat events array per run.
 *
 * Our pipeline produces more than one run (intake+match, then a reply run),
 * so runs are aggregated into a single tab group with a per-run divider on
 * the All tab. Times render in UTC and are labelled, per audit §8.4.
 */
import { useEffect, useState } from "react";

const P2P_URL = "/api/p2p/exceptions";

// Palette lifted from the mockup's :root so the look matches exactly.
const C = {
  brand: "#0070F2", shell: "#1B2735", shell2: "#354A5F",
  page: "#F5F6F7", page2: "#EAECEE", border: "#D9D9D9", borderLight: "#E5E5E5",
  text: "#1D2D3E", text2: "#556B82", text3: "#A9B4BE",
  success: "#188918", successBg: "#E8F5E9", successBorder: "#5DC122",
  error: "#BB0000", errorBg: "#FFEAEA",
  info: "#0A6ED1", infoBg: "#E5F0FA", infoBorder: "#91C8F6",
};
const MONO = 'Consolas, Menlo, monospace';

// ── formatting helpers (UTC, labelled) ────────────────────────────────────────
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour12: false, timeZone: "UTC" }) : "");
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "");
const money = (n, c) => (n == null ? "—" : `${c || "USD"} ${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const dur = (ms) => {
  const s = Math.round((ms || 0) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};
const titleCase = (s) => (s ? s.charAt(0) + s.slice(1).toLowerCase() : "");

// ── inline SVG icons (from the mockup ICON map) ───────────────────────────────
const ICON = {
  play: "M6 4l12 8-12 8z",
  mail: "M3 8l9 6 9-6M4 5h16a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z",
  send: "M22 2L11 13M22 2l-7 20-4-9-9-4z",
  file: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6",
  book: "M4 19.5A2.5 2.5 0 016.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z",
  check: "M5 13l4 4L19 7",
  x: "M6 6l12 12M18 6L6 18",
  inbox: "M22 12h-6l-2 3h-4l-2-3H2M5 5h14l3 7v7a1 1 0 01-1 1H3a1 1 0 01-1-1v-7z",
  compare: "M6 3v12M18 21V9M6 15a3 3 0 100 6 3 3 0 000-6zM18 3a3 3 0 100 6 3 3 0 000-6z",
  down: "M17 7L7 17M7 7v10h10",
  up: "M7 17L17 7M7 7h10v10",
};
const Svg = ({ name, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }}>
    <path d={ICON[name] || ICON.file} />
  </svg>
);

// marker style — driven by event_type + subtype, never by text (audit §5.2)
function markerFor(ev) {
  if (ev.status === "failed") return { bg: C.errorBg, bd: C.error, fg: C.error, icon: "x" };
  if (ev.event_type === "sop") return { bg: "#fff", bd: C.brand, fg: C.brand, icon: "book" };
  if (ev.event_type === "communication")
    return { bg: C.infoBg, bd: C.infoBorder, fg: C.info, icon: ev.communication?.direction === "inbound" ? "mail" : "send" };
  if (ev.event_subtype === "process_started" || ev.event_subtype === "started") return { bg: C.brand, bd: C.brand, fg: "#fff", icon: "play", highlight: true };
  if (/posted|voucher/i.test(ev.event_subtype || "")) return { bg: C.successBg, bd: C.successBorder, fg: C.success, icon: "check" };
  return { bg: "#fff", bd: C.border, fg: C.text2, icon: "file" };
}
const AGENT_ICON = {
  "Extraction": "file",
  "Validation": "inbox",
  "Invoice 3 Way Matching": "compare",
  "Resolution": "check",
  "Vendor / Purchaser": "mail",
};

export default function P2PTrailPanel({ exceptionId, isOpen, rowData, refreshSignal }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    if (!isOpen || !exceptionId) return;
    let cancelled = false;
    setLoading(true); setError(""); setTab("all");
    fetch(`${P2P_URL}/${exceptionId}/trail`)
      .then((r) => { if (!r.ok) throw new Error(`Trail ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e.message || "Failed to load trail"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [exceptionId, isOpen, refreshSignal]);

  if (loading) return <Shell><div style={{ padding: 24, textAlign: "center", color: C.text2 }}>Loading trail…</div></Shell>;
  if (error) return <Shell><div style={{ padding: 24, textAlign: "center", color: C.error }}>{error}</div></Shell>;
  if (!detail) return null;

  const x = detail.exception || {};
  const runs = Array.isArray(detail.runs) ? detail.runs : [];
  const hasTrail = (x && x.exception_id) || runs.some((r) => (r.events || []).length);
  if (!hasTrail) {
    return (
      <Shell>
        <div style={{ padding: "30px 16px", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: C.text }}>No agent trail yet</p>
          <small style={{ color: C.text2 }}>This exception has not been processed by the agent, or its record is no longer available.</small>
        </div>
      </Shell>
    );
  }

  // Aggregate every run into one tab group; tag each event with its run for a divider.
  const events = [];
  runs.forEach((r, ri) => (r.events || []).forEach((ev) => events.push({ ...ev, _run: ri, _pid: r.process_id })));
  const counts = runs.reduce((a, r) => ({
    all: a.all + (r.counts?.all || 0), processing: a.processing + (r.counts?.processing || 0),
    sop: a.sop + (r.counts?.sop || 0), communication: a.communication + (r.counts?.communication || 0),
  }), { all: 0, processing: 0, sop: 0, communication: 0 });
  const agentSet = new Set(events.map((e) => e.agent_name));
  const started = runs.length ? runs[0].started_at : x.created_timestamp;
  const ended = runs.length ? runs[runs.length - 1].ended_at : x.resolved_timestamp;
  const spanMs = started && ended ? Math.max(0, new Date(ended) - new Date(started)) : 0;
  const pid = runs.length ? runs[0].process_id : "";

  const statusCls = x.status === "RESOLVED" ? "ok" : ["OPEN", "INITIATED"].includes(x.status) ? "na" : "info";
  const pill = { ok: { bg: C.successBg, fg: C.success }, bad: { bg: C.errorBg, fg: C.error }, na: { bg: C.page2, fg: C.shell2 }, info: { bg: C.infoBg, fg: C.info } };

  const TABS = [
    { key: "all", label: `All ${counts.all}` },
    { key: "proc", label: `Processing ${agentSet.size}` },
    { key: "sop", label: `SOPs ${counts.sop}` },
    { key: "comm", label: `Communications ${counts.communication}` },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, fontFamily: '"72","72full",Arial,sans-serif', color: C.text }}>
      {/* Summary */}
      <section style={card}>
        <div style={cardH}>Summary</div>
        <div style={{ padding: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: "12px 16px" }}>
            <KV label="Amount"><span style={val}>{money(x.amount, x.currency)}</span></KV>
            <KV label="PO"><span style={{ ...val, fontFamily: MONO, fontSize: 13 }}>{x.po_number || "—"}</span></KV>
            <KV label="Line items"><span style={val}>{x.line_item_count ?? "—"}</span></KV>
            <KV label="Exception"><span style={{ ...val, fontSize: 14, fontWeight: 400 }}>{x.exception_name || "—"}</span></KV>
            <KV label="Overdue"><span style={{ ...val, fontSize: 14, fontWeight: 400 }}>{x.overdue ? "Yes" : "No"}</span></KV>
            <KV label="Status"><Pill {...pill[statusCls]}>{titleCase(x.status || "")}</Pill></KV>
          </div>
        </div>
      </section>

      {/* Run header + tabs */}
      <div style={{ background: "#fff", border: `1px solid ${C.borderLight}`, borderRadius: 6 }}>
        <div style={{ padding: "9px 12px", borderBottom: `1px solid ${C.borderLight}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontFamily: MONO, fontSize: 11, color: C.text2 }}>
            {runs.length > 1 ? `${runs.length} runs` : pid ? `run ${pid.slice(0, 8)}…${pid.slice(-5)}` : "run"}
          </span>
          <span style={{ color: C.text2, fontSize: 11 }}>
            {started ? `${day(started)} · ${clock(started)} → ${clock(ended)} UTC · ${dur(spanMs)}` : ""}
          </span>
        </div>
        <div style={{ padding: "9px 12px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TABS.map((t) => {
            const on = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                fontSize: 12, borderRadius: 4, padding: "4px 11px", cursor: "pointer",
                border: `1px solid ${on ? C.brand : C.border}`, background: on ? C.brand : "#fff",
                color: on ? "#fff" : C.text2, fontWeight: on ? 500 : 400,
              }}>{t.label}</button>
            );
          })}
        </div>
      </div>

      {/* Tab panel */}
      <div>
        {tab === "all" && <TabAll events={events} />}
        {tab === "proc" && <TabProcessing events={events} />}
        {tab === "sop" && <TabSops events={events} />}
        {tab === "comm" && <TabComms events={events} />}
      </div>
    </div>
  );
}

// ── little presentational bits ────────────────────────────────────────────────
const card = { background: "#fff", border: `1px solid ${C.borderLight}`, borderRadius: 6 };
const cardH = { padding: "9px 12px", borderBottom: `1px solid ${C.borderLight}`, fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: C.text2 };
const val = { fontSize: 15, fontWeight: 500, marginTop: 2, display: "block" };
const lbl = { fontSize: 11, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", color: C.text2 };
const quote = { fontSize: 13, lineHeight: 1.65, background: C.page, border: `1px solid ${C.borderLight}`, borderRadius: 6, padding: "9px 10px", marginTop: 7, whiteSpace: "pre-wrap" };

const KV = ({ label, children }) => (<div><div style={lbl}>{label}</div>{children}</div>);
const Pill = ({ bg, fg, children }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 500, borderRadius: 4, padding: "2px 9px", background: bg, color: fg, marginTop: 3 }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />{children}
  </span>
);
const Marker = ({ ev, last }) => {
  const m = markerFor(ev);
  return (
    <div style={{ position: "relative", width: 22, flex: "none" }}>
      <span style={{
        position: "absolute", left: 0, top: 0, width: 22, height: 22, borderRadius: "50%",
        background: m.bg, border: `1px solid ${m.bd}`, color: m.fg,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: m.highlight ? `0 0 0 3px rgba(0,112,242,.22)` : "none",
      }}>
        <Svg name={m.icon} size={13} />
      </span>
      {!last && <span style={{ position: "absolute", left: 11, top: 24, bottom: -13, width: 1, background: C.borderLight }} />}
    </div>
  );
};
const Empty = ({ title, desc }) => (
  <div style={card}><div style={{ padding: "26px 12px", textAlign: "center" }}>
    <p style={{ margin: 0, fontSize: 14, fontWeight: 500 }}>{title}</p>
    <small style={{ color: C.text2 }}>{desc}</small>
  </div></div>
);

// ── All tab ───────────────────────────────────────────────────────────────────
function TabAll({ events }) {
  return (
    <div style={card}><div style={{ padding: 12 }}>
      {events.map((ev, i) => {
        const sub = ev.event_type === "sop"
          ? `${ev.agent_name} · ${ev.sop?.sop_title} — ${ev.sop?.outcome}`
          : ev.event_type === "communication"
            ? `${ev.agent_name} · ${ev.communication?.direction === "inbound" ? "from " + ev.communication?.from_address : "to " + (ev.communication?.to_addresses || []).join(", ")}`
            : `${ev.agent_name}${ev.description ? " · " + ev.description : ""}`;
        const newRun = i > 0 && events[i - 1]._run !== ev._run;
        return (
          <div key={i}>
            {newRun && <div style={{ fontFamily: MONO, fontSize: 10, color: C.text3, textTransform: "uppercase", letterSpacing: ".08em", padding: "2px 0 10px 66px" }}>— reply run —</div>}
            <div style={{ display: "flex", gap: 10, paddingBottom: i < events.length - 1 ? 13 : 0 }}>
              <div style={{ width: 56, flex: "none", textAlign: "right", fontFamily: MONO, fontSize: 11, color: C.text2, paddingTop: 3 }}>{clock(ev.occurred_at)}</div>
              <Marker ev={ev} last={i === events.length - 1} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{ev.title}</div>
                <div style={{ fontSize: 12, color: C.text2, marginTop: 1 }}>{sub}</div>
                {ev.webhook_id && <div style={{ fontFamily: MONO, fontSize: 11, color: C.text3, marginTop: 5 }}>webhook {ev.webhook_id}</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div></div>
  );
}

// ── Processing tab (group by agent, with detail) ──────────────────────────────
function TabProcessing({ events }) {
  const order = [], byAgent = new Map();
  events.forEach((ev) => { if (!byAgent.has(ev.agent_name)) { byAgent.set(ev.agent_name, []); order.push(ev.agent_name); } byAgent.get(ev.agent_name).push(ev); });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {order.map((name) => {
        const evs = byAgent.get(name);
        const start = new Date(evs[0].occurred_at), end = new Date(evs[evs.length - 1].occurred_at);
        const failed = evs.some((e) => e.status === "failed");
        const kinds = { sop: 0, communication: 0, processing: 0 };
        evs.forEach((e) => { kinds[e.event_type] = (kinds[e.event_type] || 0) + 1; });
        const chips = [
          `${evs.length} step${evs.length === 1 ? "" : "s"}`,
          kinds.sop ? `${kinds.sop} SOP` : null,
          kinds.communication ? `${kinds.communication} email` : null,
        ].filter(Boolean).join(" · ");
        return (
          <div key={name} style={card}><div style={{ padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 600 }}>
                <span style={{ width: 26, height: 26, borderRadius: "50%", background: failed ? C.errorBg : C.infoBg, color: failed ? C.error : C.info, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Svg name={AGENT_ICON[name] || "file"} size={14} />
                </span>{name}
              </div>
              <span style={{ fontFamily: MONO, color: C.text2, fontSize: 11, whiteSpace: "nowrap" }}>
                {clock(start.toISOString())}→{clock(end.toISOString())} · {dur(end - start)}
              </span>
            </div>
            <div style={{ fontFamily: MONO, fontSize: 10.5, color: C.text3, marginTop: 5, paddingLeft: 34, textTransform: "uppercase", letterSpacing: ".04em" }}>{chips}</div>
            <div style={{ marginTop: 9, paddingLeft: 34, display: "flex", flexDirection: "column", gap: 7 }}>
              {evs.map((ev, i) => (
                <div key={i} style={{ display: "flex", gap: 8 }}>
                  <span style={{ fontFamily: MONO, color: C.text3, fontSize: 11, paddingTop: 1, flex: "none" }}>{clock(ev.occurred_at)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, color: C.text }}>
                      {ev.event_type === "sop" && <span style={{ fontFamily: MONO, fontSize: 10, color: "#0C447C", background: "#E6F1FB", borderRadius: 3, padding: "0 4px", marginRight: 5 }}>SOP</span>}
                      {ev.event_type === "communication" && <span style={{ fontFamily: MONO, fontSize: 10, color: C.info, background: C.infoBg, borderRadius: 3, padding: "0 4px", marginRight: 5 }}>{ev.communication?.direction === "inbound" ? "IN" : "OUT"}</span>}
                      {ev.title}
                    </div>
                    {ev.description && <div style={{ fontSize: 11.5, color: C.text2, marginTop: 1 }}>{ev.description}</div>}
                    {ev.event_type === "communication" && ev.communication?.subject && <div style={{ fontSize: 11.5, color: C.text2, marginTop: 1 }}>“{ev.communication.subject}”</div>}
                    {ev.webhook_id && <div style={{ fontFamily: MONO, fontSize: 10.5, color: C.text3, marginTop: 1 }}>webhook {ev.webhook_id}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div></div>
        );
      })}
    </div>
  );
}

// ── SOPs tab ──────────────────────────────────────────────────────────────────
function TabSops({ events }) {
  const sops = events.filter((e) => e.event_type === "sop");
  if (!sops.length) return <Empty title="No SOPs evaluated" desc="The agent closed this invoice without applying a rule." />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {sops.map((ev, i) => {
        const s = ev.sop || {};
        const cls = s.outcome === "passed" ? "ok" : s.outcome === "breached" ? "bad" : "na";
        const label = s.outcome === "passed" ? "Passed" : s.outcome === "breached" ? "Breached" : "Not applicable";
        const p = { ok: { bg: C.successBg, fg: C.success }, bad: { bg: C.errorBg, fg: C.error }, na: { bg: C.page2, fg: C.shell2 } }[cls];
        return (
          <div key={i} style={card}><div style={{ padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                <span style={{ fontFamily: MONO, fontSize: 11, color: "#0C447C", background: "#E6F1FB", borderRadius: 4, padding: "1px 6px" }}>{s.sop_code}</span>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{s.sop_title}</span>
              </div>
              <Pill {...p}>{label}</Pill>
            </div>
            <div style={{ fontSize: 12, color: C.text2, marginTop: 8 }}>Rule — {s.rule_text}</div>
            <div style={quote}>Conclusion — {s.conclusion_text}</div>
            {s.evidence && <Evidence ev={s.evidence} />}
            <div style={{ fontFamily: MONO, fontSize: 11, color: C.text3, marginTop: 6 }}>evaluated {clock(ev.occurred_at)} · {ev.agent_name}</div>
          </div></div>
        );
      })}
    </div>
  );
}

// Structured backing for an SOP conclusion — renders whatever evidence keys the
// agent wrote, in order. Labels are just a display of the key; values are verbatim.
function Evidence({ ev }) {
  if (!ev || typeof ev !== "object") return null;
  const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const fmt = (k, v) => {
    if (v == null || v === "") return null;
    if (/amount|tolerance|usd/i.test(k) && !isNaN(Number(v))) return `USD ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (/pct|percent/i.test(k) && !isNaN(Number(v))) return `${Number(v).toFixed(2)}%`;
    return String(v);
  };
  const rows = Object.entries(ev).map(([k, v]) => [label(k), fmt(k, v)]).filter(([, v]) => v != null);
  if (!rows.length) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 18px", marginTop: 8, padding: "8px 10px", background: C.page, border: `1px solid ${C.borderLight}`, borderRadius: 6 }}>
      {rows.map(([k, v]) => (
        <div key={k} style={{ fontSize: 12 }}>
          <span style={{ color: C.text2 }}>{k}: </span>
          <span style={{ color: C.text, fontWeight: 500, fontFamily: MONO }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

// ── Communications tab ────────────────────────────────────────────────────────
function TabComms({ events }) {
  const comms = events.filter((e) => e.event_type === "communication");
  if (!comms.length) return <Empty title="No messages" desc="Nothing was sent to or received from this vendor." />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {comms.map((ev, i) => {
        const c = ev.communication || {};
        const inb = c.direction === "inbound";
        return (
          <div key={i} style={card}><div style={{ padding: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500 }}>
                <span style={{ width: 24, height: 24, borderRadius: "50%", background: C.infoBg, color: C.info, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Svg name={inb ? "down" : "up"} size={13} />
                </span>{inb ? "Received" : "Sent"}
              </div>
              <span style={{ fontFamily: MONO, color: C.text2, fontSize: 11 }}>{clock(ev.occurred_at)}</span>
            </div>
            <div style={{ fontSize: 12, color: C.text2, marginTop: 8 }}>{c.from_address} → {(c.to_addresses || []).join(", ")}</div>
            {c.subject && <div style={{ fontSize: 13, fontWeight: 500, marginTop: 3 }}>{c.subject}</div>}
            <div style={{ ...quote, color: C.text2 }}>{c.body_text}</div>
            {Array.isArray(c.attachments) && c.attachments.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                {c.attachments.map((a, j) => (
                  <span key={j} style={{ fontSize: 11, padding: "3px 8px", display: "inline-flex", gap: 5, alignItems: "center", border: `1px solid ${C.border}`, borderRadius: 6, color: C.brand }}>
                    <Svg name="file" size={12} />{a.filename}
                  </span>
                ))}
              </div>
            )}
          </div></div>
        );
      })}
    </div>
  );
}

// wraps loading / error states in a neutral card so layout stays stable
const Shell = ({ children }) => <div style={card}>{children}</div>;
